<script setup>
import { onMounted, onUnmounted, watch } from "vue";
import { useScrollStore } from "@/stores/storeScroll";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { useQuizWordsStore } from "@/stores/storeQuizWords";
import { useModals } from "@/composables/useModals";
import { sendMetrik, trackPixel } from "@/utils/metrika";
import { useGameTimers } from "@/composables/useGameTimers";
import QuizEndGameWords from "@/components/quize/QuizEndGameWords.vue";
import QuizWelcomeScreen from "@/components/quize/QuizWelcomeScreen.vue";
import QuizWords from "@/components/quize/QuizWords.vue";
import BaseButtonUploadReceipt from "@/components/ui/BaseButtonUploadReceipt.vue";
import QuizWordsTimer from "@/components/quize/QuizWordsTimer.vue";

const storeScroll = useScrollStore();
const storeSettings = useSettingsStore();
const storeRequests = useRequestsStore();
const storeQuizWords = useQuizWordsStore();
const { openModal } = useModals();
const { timerPoints, timerSeries } = useGameTimers();

onMounted(() => {
	trackPixel('VIEW', 'games');
});

watch(
	() => storeRequests.userData,
	(userData) => {
		if (userData?.id) {
			storeQuizWords.progressGame();
		}
	},
	{ immediate: true }
);

watch(
	() => storeQuizWords.gameProgress,
	(progress) => {
		if (progress?.series?.points_expire_remaining_seconds) {
			timerPoints.start(progress.series.points_expire_remaining_seconds);
		}
		if (progress?.series?.series_remaining_seconds) {
			timerSeries.start(progress.series.series_remaining_seconds);
		}
	}
);

onUnmounted(() => {
	storeQuizWords.resetGame();
});
</script>
<template>
	<section class="quiz" :style="{ paddingTop: storeScroll.paddingTop }">
		<TransitionGroup>
			<QuizWelcomeScreen v-if="storeQuizWords.isShowWelcomScreen" class="quiz-words">
				<template #nameGame>Игра «Листопад»</template>					
				<template #descLevel><span class="quiz-words__subtitle" v-if="storeQuizWords.gameProgress?.current_level > 1">Уровень: <i>{{ storeQuizWords.gameProgress?.current_level }}</i></span></template>
				<template #descGame><span class="quiz-words__subtitle">Как играть?</span></template>
				<template #btnStartGame>
					<ul class="quiz-words__list">
						<li>Составляйте слова, соединяя буквы.</li>
						<li>Проходите уровни и зарабатывайте листочки.</li>
						<li>Одна буква может использоваться несколько раз.</li>
						<li>Загружайте чеки, чтобы добавить листочки к общему балансу!</li>
					</ul>
					<button @click="storeQuizWords.startGame()" class="btn">Начать игру</button>
					<div class="quiz-words__notice" v-if="storeQuizWords.gameProgress?.series">
						<p class="quiz-words__notice-text">Для начисления игровых листочков в общий баланс необходим принятый чек.</p> 
						<QuizWordsTimer :count="storeQuizWords.gameProgress?.series?.points_earned" :time="timerPoints.remaining.value.hours + ':' + timerPoints.remaining.value.minutes" />
						<BaseButtonUploadReceipt type-btn="click_leaf_fall_lk" class="quiz-matches__btn --small">Загрузить чек</BaseButtonUploadReceipt>
					</div>
					<div class="quiz-words__notice" v-if="storeQuizWords.statusActive">
						<p class="quiz-words__notice-text --small">Чтобы сохранить прогресс серии, пройдите уровень.</p>
						<QuizWordsTimer :is-fire="false" :count="storeQuizWords.gameProgress?.series?.points_earned" :time="timerSeries.remaining.value.hours + ':' + timerSeries.remaining.value.minutes" />
						<button @click="storeQuizWords.startGame()" class="btn quiz-matches__btn --small">Продолжить</button>
					</div>
				</template>
				<template #imgGame>
					<picture class="">
						<source media="(max-width: 480px)" srcset="@/assets/img/games/start_words_mobile.webp" />
						<img class="full-img" width="549" height="549" src="@/assets/img/games/start_words.webp" alt="изображение" />
					</picture>
				</template>
			</QuizWelcomeScreen>

			<QuizWords v-if="storeQuizWords.isShowGame" />

			<QuizEndGameWords v-if="storeQuizWords.isShowEndGame" />
		</TransitionGroup>
	</section>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.quiz {
	background-image: url("@/assets/img/games/welcome_game_bg.webp");
	background-repeat: no-repeat;
	background-position: center; /* x y*/
	background-size: cover; /* w h*/
	@media (max-width: vars.$xx) {
		background-image: none;
	}
}
.quiz-words {
	:deep(.quiz-welcome__content) {
		padding-top: 5rem;
		@media (max-width: vars.$x480) {
			padding-top: 0;
		}
	}
	:deep(.quiz-welcome__img) {
		@media (max-width: vars.$x480) {
			height: 14.5rem;
		}
	}
	&__subtitle {
		font-family: var(--second-family);
		font-weight: 700;
		font-size: 2rem;
		line-height: 110%;
		text-transform: uppercase;
		text-align: right;
		color: var(--text-accent-2-100);
		i {
			font-style: normal;
			color: #fff;
		}
	}
	&__list {
		padding-left: 10px;
		@media (max-width: vars.$x480) {
			font-size: 1.6rem;
		}
		>li {
			display: flex;
			align-items: center;
			gap: 10px;
			&::before {
				content: '';
				background-color: currentColor;
				width: 5px;
				height: 5px;
				border-radius: 100%;
				// margin-right: 10px;
			}
			&:not(:last-child) {
				margin-bottom: 5px;
			}
		}
	}
	&__notice {
		display: grid;
		gap: 20px;
		border-top: 1px solid var(--border-accent-2-1);
		padding-top: 4rem;
		@media (max-width: vars.$x480) {
			padding-top: 2.4rem;
			justify-items: center;
		}
	}
	&__notice-text {
		@media (max-width: vars.$x480) {
			font-size: 1.6rem;
			text-align: center;
			line-height: 140%;
		}
		&--small {
			font-size: 1.6rem;
			@media (max-width: vars.$x480) {
				text-align: center;
				line-height: 140%;
			}
		}
	}

}

.v-enter-active,
.v-leave-active {
	transition: 0.5s ease;
}

.v-enter-from,
.v-leave-to {
	opacity: 0;
	transform: translateY(10px);
}



</style>
